/* eslint-disable @typescript-eslint/naming-convention */
import { ActivatedRoute, Router } from '@angular/router';
import { Component, EventEmitter, OnInit, Output, Renderer2, ViewEncapsulation } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client';

import { PageContent } from '../../models/page-content.model';
import { SEOStrapiService } from '../../../common/services/seo-strapi.service';
import { ISEOLandingPageCollection } from '../../../common/interfaces/seo-landing-page-collection.interface';
import { SectionService } from '../../services/section.service';
import { GlobalElementType } from '../../enums/global-element-type-uid.enum';
import { Settings } from '../../enums/settings.enum';
import { SeoService } from '@challenger/care/services/seo.service';
import { CareAnalyticsService } from '@challenger/care/services/care-analytics.service';
import { AppLoadingService } from '../../../common/services/app-loading.service';
import { LiveChatBot } from '../../models/live-chat-bot.model';
import { LiveChatService } from '@challenger/care/services/live-chat.service';
import { ModalType } from '@challenger6/vm-pattern-library/modal/modal.type';
import { ModalService } from '../../../common/services/modal.service';
import { PageSection } from '../../models/page-section.model';
import { Setting } from '../../models/setting.model';
import { map } from 'rxjs';
import { SessionService } from '@challenger/care/services/session.service';
import { HeaderVariant } from '@challenger/care/components/header-wrapper/header.types';
import { AuthService } from '@challenger/care/services/auth.service';

@Component({
  selector: 'vm-seo-landing-page-shell',
  templateUrl: './seo-landing-page-shell.component.html',
  styleUrls: ['./seo-landing-page-shell.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SEOLandingPageShellComponent implements OnInit {
  @Output() contentClick = new EventEmitter();
  @Output() continue = new EventEmitter();
  @Output() selectedCard = new EventEmitter();

  backLabel = 'Back';
  content: PageContent[] = [];
  didThisHelpArticleId = 'angular-did-this-help';
  isLoading = true;
  showAPILoading = false;  
  showModal: string;

  type = GlobalElementType;
  modalType = ModalType;
  settings = Settings;

  liveChatBots = new Array<LiveChatBot>();

  dataLayerClicks: any = {
    accordion: {
      event: 'accordion_actions',
      event_name: 'accordion_open',
      event_params: {
        content_type: 'Web - Care - Faults',
      },
    },
    button: {
      event: 'content_actions',
      event_name: 'content_clicked',
      event_params: {
        content_type: 'web - faults - alternative support options',
        content_link: window.location.href.split('?')[0],
      },
    },
  };

  constructor(
    private sectionService: SectionService,
    private seoStrapiService: SEOStrapiService,
    private seoService: SeoService,
    private route: ActivatedRoute,
    private careAnalyticsService: CareAnalyticsService,
    private router: Router,
    private renderer: Renderer2,
    private appLoadingService: AppLoadingService,
    private liveChatService: LiveChatService,
    private modalService: ModalService,
    private sessionService: SessionService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.appLoadingService.showLoading$.subscribe((isLoading) => {
      this.isLoading = isLoading;
    });
    this.liveChatService.registerChatBot$.subscribe((chatBotId) => {
      if (this.liveChatBots.every((bots) => bots.chatBotId !== chatBotId)) {
        this.liveChatBots.push({
          chatBotId,
          chatContainer: this.liveChatService.createChatContainer(this.renderer, chatBotId),
        });
        this.liveChatService.reloadLiveAgentMonitor();
      }
    });
    this.liveChatService.showChatBot$.subscribe((chatBotId) => {
      const liveChatBot = this.liveChatBots.find((bots) => bots.chatBotId === chatBotId);
      if (liveChatBot) {
        const liveChatBotClickElement = liveChatBot.chatContainer?.querySelector(
          '[data-lp-event="click"]'
        ) as HTMLDivElement | null;
        if (liveChatBotClickElement) {
          liveChatBotClickElement.click();
        }
      }
    });
    this.modalService.showModal$.subscribe((modalId) => {
      this.showModal = modalId;
    });
    this.sessionService
      .getSessionData(undefined, false)
      .pipe(
        map((sesData) => {
          this.fetchContent(sesData);
        })
      )
      .subscribe();
  }

  fetchContent(sesData: any) {
    this.route.data.subscribe((dataParam) => {
      const strapiUID =
        this.route.snapshot.paramMap.get('strapiUID')?.toLowerCase() || dataParam?.strapiUID?.toLowerCase();

      const strapiLevel1Prefix =
        this.route.snapshot.paramMap.get('strapiLevel1Prefix') || dataParam?.strapiLevel1Prefix?.toLowerCase()
          ? `${
              this.route.snapshot.paramMap.get('strapiLevel1Prefix')?.toLowerCase() ||
              dataParam?.strapiLevel1Prefix?.toLowerCase()
            }/`
          : '';
      const strapiLevel2Prefix = this.route.snapshot.paramMap.get('strapiLevel2Prefix')
        ? `${this.route.snapshot.paramMap.get('strapiLevel2Prefix')?.toLowerCase()}/`
        : '';
      const strapiLevel3Prefix = this.route.snapshot.paramMap.get('strapiLevel3Prefix')
        ? `${this.route.snapshot.paramMap.get('strapiLevel3Prefix')?.toLowerCase()}/`
        : '';
      const strapiLevel4Prefix = this.route.snapshot.paramMap.get('strapiLevel4Prefix')
        ? `${this.route.snapshot.paramMap.get('strapiLevel4Prefix')?.toLowerCase()}/`
        : '';

      const strapiPrefixUID =
        `${strapiLevel1Prefix}${strapiLevel2Prefix}${strapiLevel3Prefix}${strapiLevel4Prefix}`?.slice(0, -1);

      this.seoStrapiService
        .getContent(strapiPrefixUID, strapiUID)
        .subscribe((result: ApolloQueryResult<ISEOLandingPageCollection>) => {
          const seoLandingPages = result?.data?.seoLandingPages;

          if (seoLandingPages === undefined || seoLandingPages?.length < 1) {
            this.router.navigate(['./page-not-found']);
          }

          this.seoService.updatePageTitle(seoLandingPages[0]?.titleOfTheBrowser);
          this.seoService.updateMetaTags(seoLandingPages[0]?.MetaTagSection);
          const uidList = this.getDeviceUIDList(seoLandingPages[0]);

          if (uidList.length === 0) {
            const pageContent = new PageContent();
            const pageSettings = this.sectionService.getPageSettings(seoLandingPages[0]);
            if (this.isSetting(pageSettings, Settings.ThemeOverride, ['black friday'])) {
              pageContent.hero = this.sectionService.getHeroContent(this.renderer, seoLandingPages[0], 'black');
            } else {
              pageContent.hero = this.sectionService.getHeroContent(this.renderer, seoLandingPages[0]);
            }

            this.appLoadingService.setTheme(
              pageContent?.hero !== null && pageContent?.hero?.background !== 'plum'
                ? HeaderVariant.primary
                : HeaderVariant.secondary
            );
            seoLandingPages[0]?.elements.forEach((data) => {
              pageContent.items.push(
                this.sectionService.getComponentContent(
                  data?.globalElementType.uid || data?.globalElementType.title,
                  data,
                  sesData
                )
              );
            });
            pageContent.settings = pageSettings;
            this.content.push(pageContent);

            this.appLoadingService.showLoading(false);
          } else {
            uidList.unshift(strapiUID);
            this.seoStrapiService
              .getContent(strapiPrefixUID, uidList)
              .subscribe((results: ApolloQueryResult<ISEOLandingPageCollection>) => {
                results?.data?.seoLandingPages.forEach((element, index) => {
                  const pageContent = new PageContent();
                  pageContent.uid = element.uid;
                  results?.data?.seoLandingPages[index]?.elements.forEach((data) => {
                    pageContent.items.push(
                      this.sectionService.getComponentContent(
                        data?.globalElementType.uid || data?.globalElementType.title,
                        data,
                        sesData
                      )
                    );
                  });

                  if (strapiUID === element.uid) {
                    // set the header/main artcle first
                    this.content.unshift({
                      ...pageContent,
                      ...{ hero: this.sectionService.getHeroContent(this.renderer, element) },
                    });
                    this.appLoadingService.setTheme(
                      pageContent?.hero !== null && pageContent?.hero?.background !== 'plum'
                        ? HeaderVariant.primary
                        : HeaderVariant.secondary
                    );
                  } else {
                    this.content.push(pageContent);
                  }
                  this.appLoadingService.showLoading(false);
                });
              });
          }
        });
    });
  }

  public getDeviceUIDList(article: any) {
    const uidList = new Array<string>();
    if (article?.elements[0]?.globalElementType?.uid === GlobalElementType.TileFullWidthClickable) {
      article?.elements[0]?.genericComponent.forEach((generic) => {
        const uid = generic?.uid;
        if (!uidList.includes(uid)) {
          uidList.push(uid);
        }
      });
    }
    return uidList;
  }

  onContinue(next: string) {
    if (next) {
      if (next.toLowerCase().startsWith('modal:')) {
        this.openModal(null, next.replace('modal:', ''));
      } else if (next?.includes('https://')) {
        window.location.href = next;
      } else {
        this.router.navigate([next], { relativeTo: this.route });
      }
    }
  }

  onContentClicked(content: any) {
    const eventParamsMerged = { ...this.dataLayerClicks[content.type].event_params, ...content.eventParams };
    this.careAnalyticsService.addContentEventToDataLayer(
      this.dataLayerClicks[content.type].event,
      this.dataLayerClicks[content.type].event_name,
      eventParamsMerged
    );
  }

  public onSelectedCard(event) {
    const id = event.productId;
    document.querySelectorAll('.topic-items').forEach((item) => {
      if (event.productId === item.id) {
        item.classList.add('show');
        item.classList.remove('hide');
      } else {
        item.classList.add('hide');
        item.classList.remove('show');
      }
    });
    document.getElementById(id).scrollIntoView({ behavior: 'smooth' });
    this.onContentClicked({ type: 'button', event_params: { content_name: id } });
  }

  openModal(e, id): boolean {
    // this.priorityCallService.createPriorityCallMTD(e);
    if (e) {
      e.preventDefault();
    }
    this.showModal = id;
    return false;
  }

  getModalSections(sections: PageSection[]): PageSection[] {
    const result = sections.filter((section) => section?.type === GlobalElementType.ModalSection);
    return result;
  }

  getNonModalSections(sections: PageSection[]): PageSection[] {
    return sections.filter((section) => section?.type !== GlobalElementType.ModalSection);
  }

  checkDividerSettings(settings: Setting[]): boolean {
    if (settings && settings?.length > 0) {
      const setting = settings.find((item) => item.key === Settings.HasDivider);
      return setting ? setting.value : true;
    }
    return true;
  }

  isSetting(settings: Setting[], settingKey: Settings, matches: Array<string>) {
    if (settings && settings?.length > 0) {
      const setting = settings?.find((item) => item?.key === settingKey);
      return setting ? matches.includes(setting.value) : false;
    }
    return false;
  }

  addClasses(settings: Setting[]) {
    if (settings && settings?.length > 0) {
      const setting = settings?.find((item) => item?.key === Settings.AddClass);
      return setting?.value;
    }
    return null;
  }

  setAPILoadingState(value: boolean) {
    this.showAPILoading = value;
  }
}
